/**
 * Redux slice for medical PA orders fetched from Postgres.
 *
 * Manages paginated search results including loading, error, and inline-loader states.
 * Provides thunks for initial fetch, forward/backward pagination, and multi-page catch-up.
 * @module medicalPaOrdersFromPostgres
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../shared/api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { PostgresResponseForMedicalPaOrders } from "../../../data-model/postgresResponseForMedicalPaOrders";

/** State shape for the Postgres-backed medical PA orders slice. */
interface MedicalPaOrdersFromPostgresState {
  data?: PostgresResponseForMedicalPaOrders;
  loading?: boolean;
  error?: string;
  /** When true, shows an inline loader instead of the full-page skeleton. */
  showinLineLoader?: boolean;
}

const initialState: MedicalPaOrdersFromPostgresState = {
  data: undefined,
  loading: false,
  error: undefined,
  showinLineLoader: false,
};

const medicalPaOrdersFromPostgresSlice = createSlice({
  name: "medicalPaOrdersFromPostgres",
  initialState,
  reducers: {
    setMedicalPaOrdersFromPostgresData: (
      state,
      action: PayloadAction<PostgresResponseForMedicalPaOrders | undefined>,
    ) => {
      state.data = action.payload;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.data = undefined;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
      state.loading = false;
      state.data = undefined;
      state.showinLineLoader = false;
    },
    resetMedicalPaOrdersFromPostgresData: (state) => {
      state.data = undefined;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
    prependDataFromPostgres: (
      state,
      action: PayloadAction<PostgresResponseForMedicalPaOrders>,
    ) => {
      state.data = state.data
        ? {
            ...action.payload,
            hits: [
              ...(action.payload.hits ?? []),
              ...(state?.data?.hits ?? []),
            ],
          }
        : action.payload;
      state.loading = false;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    appendDataFromPostgres: (
      state,
      action: PayloadAction<PostgresResponseForMedicalPaOrders>,
    ) => {
      state.data = state.data
        ? {
            ...action.payload,
            hits: [
              ...(state?.data?.hits ?? []),
              ...(action.payload.hits ?? []),
            ],
          }
        : action.payload;
      state.error = undefined;
      state.showinLineLoader = false;
    },
    setLoadingFalse: (state) => {
      state.loading = false;
    },
  },
});

export const {
  setMedicalPaOrdersFromPostgresData,
  setLoading,
  setError,
  resetMedicalPaOrdersFromPostgresData,
  setinLineLoader,
  prependDataFromPostgres,
  appendDataFromPostgres,
  setLoadingFalse,
} = medicalPaOrdersFromPostgresSlice.actions;

/**
 * Thunk: fetches the initial page of medical PA orders from Postgres.
 * No-ops if a fetch is already in progress.
 *
 * @param payload - Search/filter/pagination parameters.
 */
export const fetchMedicalPaOrdersFromPostgresData =
  (payload: SearchForMedicalPaOrdersFromPostgresParams) =>
  async (dispatch, getState: any) => {
    const { medicalPaOrdersFromPostgres } = getState();
    if (medicalPaOrdersFromPostgres.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await searchForMedicalPaOrdersFromPostgres(payload);
      dispatch(
        setMedicalPaOrdersFromPostgresData(
          JSON.parse(response) as PostgresResponseForMedicalPaOrders,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

/**
 * Thunk: fetches the next page and appends results to existing hits (infinite scroll forward).
 *
 * @param payload - Search/filter/pagination parameters for the next page.
 */
export const fetchMoreMedicalPaOrdersFromPostgresData =
  (payload: SearchForMedicalPaOrdersFromPostgresParams) =>
  async (dispatch, getState: any) => {
    const { medicalPaOrdersFromPostgres } = getState();
    if (medicalPaOrdersFromPostgres.loading) {
      return;
    }
    dispatch(setinLineLoader(true));
    try {
      const response = await searchForMedicalPaOrdersFromPostgres(payload);
      dispatch(
        appendDataFromPostgres(
          JSON.parse(response) as PostgresResponseForMedicalPaOrders,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

/**
 * Thunk: fetches a previous page and prepends results to existing hits (scroll backward).
 *
 * @param payload - Search/filter/pagination parameters for the previous page.
 */
export const fetchPreviousMedicalPaOrdersFromPostgresData =
  (payload: SearchForMedicalPaOrdersFromPostgresParams) =>
  async (dispatch, getState: any) => {
    const { medicalPaOrdersFromPostgres } = getState();
    if (medicalPaOrdersFromPostgres.loading) {
      return;
    }
    dispatch(setinLineLoader(true));
    try {
      const response = await searchForMedicalPaOrdersFromPostgres(payload);
      dispatch(
        prependDataFromPostgres(
          JSON.parse(response) as PostgresResponseForMedicalPaOrders,
        ),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

/**
 * Thunk: sequentially fetches multiple pages and appends them to build up to a given page.
 * Useful when navigating directly to page N to pre-populate all preceding pages.
 *
 * @param payloads - Array of per-page search parameters, executed in order.
 */
export const fetchDataUptoGivenPageNumberFromMedicalPaOrdersFromPostgres =
  (payloads: SearchForMedicalPaOrdersFromPostgresParams[]) =>
  async (dispatch, getState: any) => {
    const { medicalPaOrdersFromPostgres } = getState();
    if (medicalPaOrdersFromPostgres.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      for (const params of payloads) {
        const response = await searchForMedicalPaOrdersFromPostgres(params);
        const parsed = JSON.parse(
          response,
        ) as PostgresResponseForMedicalPaOrders;
        dispatch(appendDataFromPostgres(parsed));
      }
    } catch (error) {
      dispatch(setError(error as string));
    } finally {
      dispatch(setLoadingFalse());
    }
  };

export default medicalPaOrdersFromPostgresSlice.reducer;
